import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi, uploadFileToSignedUrl } from '../lib/api.ts';
import { Btn } from '../components/Btn.tsx';
import { CenteredSpinner } from '../components/Spinner.tsx';
import { Field, TextArea, TextInput } from '../components/Field.tsx';
import { ErrorMessage } from '../components/ErrorMessage.tsx';
import { EmptyState } from '../components/EmptyState.tsx';
import { useBranding } from '../lib/branding.tsx';
import { COMPANY_INFO_LABEL_DEFAULTS, type Branding } from '@l2u/shared/schemas';
import type { QuestionCategory } from '../lib/types.ts';

const HEX_RE = /^#([0-9a-fA-F]{6}|[0-9a-fA-F]{3})$/;

export function AdminSettings() {
  const qc = useQueryClient();
  const { reload } = useBranding();
  const { data, isLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: () => adminApi.get<{ branding: Partial<Branding> }>('/api/settings'),
  });

  const [form, setForm] = useState<Partial<Branding>>({});
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    if (data) setForm({ ...data.branding });
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: (input: Partial<Branding>) => adminApi.put('/api/settings', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['settings'] });
      void reload();
    },
    onError: (err) => setError(err),
  });

  const logoMutation = useMutation({
    mutationFn: async (file: File) => {
      const presigned = await adminApi.post<{ url: string; public_url: string }>(
        '/api/settings/logo-upload-url',
        { file_name: file.name, mime_type: file.type },
      );
      await uploadFileToSignedUrl(presigned.url, file);
      return presigned.public_url;
    },
    onSuccess: (publicUrl) => {
      setForm((f) => ({ ...f, logoUrl: publicUrl }));
    },
    onError: (err) => setError(err),
  });

  if (isLoading) return <CenteredSpinner />;

  function set<K extends keyof Branding>(key: K, value: Branding[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function validate(): string | null {
    if (form.primaryColor && !HEX_RE.test(form.primaryColor)) return 'Primary colour must be a hex value';
    if (form.primaryDark && !HEX_RE.test(form.primaryDark)) return 'Primary dark must be a hex value';
    if (form.accentColor && !HEX_RE.test(form.accentColor)) return 'Accent colour must be a hex value';
    return null;
  }

  function save() {
    const err = validate();
    if (err) {
      setError(new Error(err));
      return;
    }
    setError(null);
    saveMutation.mutate(form);
  }

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Settings</h1>
          <p className="mt-1 text-sm text-slate-500">Branding visible to companies and in emails.</p>
        </div>
        <Btn onClick={save} loading={saveMutation.isPending}>Save</Btn>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-4 rounded-xl border border-slate-200 bg-white p-6">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Identity</h2>
          <Field label="Company name">
            <TextInput value={form.companyName ?? ''} onChange={(e) => set('companyName', e.target.value)} />
          </Field>
          <Field label="Tagline">
            <TextInput value={form.tagline ?? ''} onChange={(e) => set('tagline', e.target.value)} />
          </Field>
          <Field label="Logo">
            <div className="flex items-center gap-3">
              {form.logoUrl && (
                <img src={form.logoUrl} alt="Logo" className="h-10 w-auto rounded border border-slate-200" />
              )}
              <input
                type="file"
                accept="image/png,image/jpeg,image/svg+xml,image/webp"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) logoMutation.mutate(f);
                }}
                className="text-sm"
                disabled={logoMutation.isPending}
              />
              {logoMutation.isPending && <span className="text-xs text-slate-500">Uploading…</span>}
            </div>
          </Field>

          <h2 className="mt-6 text-sm font-semibold uppercase tracking-wide text-slate-500">Colours</h2>
          <div className="grid grid-cols-3 gap-3">
            <ColourField label="Primary" value={form.primaryColor} onChange={(v) => set('primaryColor', v)} />
            <ColourField label="Primary dark" value={form.primaryDark} onChange={(v) => set('primaryDark', v)} />
            <ColourField label="Accent" value={form.accentColor} onChange={(v) => set('accentColor', v)} />
          </div>

          <h2 className="mt-6 text-sm font-semibold uppercase tracking-wide text-slate-500">Welcome screen</h2>
          <Field label="Heading">
            <TextInput value={form.welcomeHeading ?? ''} onChange={(e) => set('welcomeHeading', e.target.value)} />
          </Field>
          <Field label="Message">
            <TextArea value={form.welcomeMessage ?? ''} onChange={(e) => set('welcomeMessage', e.target.value)} />
          </Field>

          <h2 className="mt-6 text-sm font-semibold uppercase tracking-wide text-slate-500">Email sender</h2>
          <div className="grid grid-cols-2 gap-3">
            <Field label="From name">
              <TextInput value={form.emailFromName ?? ''} onChange={(e) => set('emailFromName', e.target.value)} />
            </Field>
            <Field label="From address">
              <TextInput type="email" value={form.emailFromAddress ?? ''} onChange={(e) => set('emailFromAddress', e.target.value)} />
            </Field>
          </div>

          <ErrorMessage error={error} />
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-6">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">Preview</h2>
          <div
            className="rounded-lg p-6 text-white"
            style={{ backgroundColor: form.primaryColor ?? '#4D148C' }}
          >
            {form.logoUrl && <img src={form.logoUrl} alt="" className="mb-3 h-10 w-auto" />}
            <div className="text-lg font-semibold">{form.companyName ?? 'Company name'}</div>
            {form.tagline && <div className="text-sm opacity-80">{form.tagline}</div>}
          </div>
          <div className="mt-4 rounded-lg border border-slate-200 p-4">
            <div className="text-base font-semibold text-slate-900">{form.welcomeHeading ?? 'Welcome heading'}</div>
            <div className="mt-1 text-sm text-slate-600">{form.welcomeMessage ?? 'Welcome message'}</div>
            <button
              className="mt-3 rounded-md px-3 py-1.5 text-sm font-medium text-white"
              style={{ backgroundColor: form.accentColor ?? '#FF6600' }}
            >
              Sample button
            </button>
          </div>
          <div className="mt-3 text-xs text-slate-500">
            Email will be sent as “{form.emailFromName ?? 'Company name'}” &lt;{form.emailFromAddress ?? 'noreply@example.com'}&gt;
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <CompanyInfoSettingsPanel form={form} set={set} />
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <QuestionCategoriesPanel />
      </div>
    </div>
  );
}

function CompanyInfoSettingsPanel({
  form,
  set,
}: {
  form: Partial<Branding>;
  set: <K extends keyof Branding>(key: K, value: Branding[K]) => void;
}) {
  const labels = form.companyInfoLabels ?? {};
  const stations = form.stationOptions ?? [];

  function setLabel(key: keyof typeof COMPANY_INFO_LABEL_DEFAULTS, value: string) {
    const trimmed = value.trim();
    const next = { ...(form.companyInfoLabels ?? {}) };
    if (trimmed.length === 0) {
      delete (next as Record<string, unknown>)[key];
    } else {
      (next as Record<string, string>)[key] = trimmed;
    }
    set('companyInfoLabels', Object.keys(next).length === 0 ? undefined : next);
  }

  function setStations(value: string) {
    const list = value
      .split('\n')
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    set('stationOptions', list.length === 0 ? undefined : list);
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
          Company information
        </h2>
        <p className="mt-1 text-xs text-slate-500">
          Rename the labels shown on the admin invite form and the company&apos;s
          questionnaire. Leave blank to use the default label.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {(Object.entries(COMPANY_INFO_LABEL_DEFAULTS) as [
          keyof typeof COMPANY_INFO_LABEL_DEFAULTS,
          string,
        ][]).map(([key, fallback]) => (
          <Field key={key} label={`${key} label`} hint={`Default: ${fallback}`}>
            <TextInput
              value={labels[key] ?? ''}
              placeholder={fallback}
              onChange={(e) => setLabel(key, e.target.value)}
            />
          </Field>
        ))}
      </div>

      <Field
        label="Station options"
        hint="One station per line. These become the dropdown choices admins pick from when inviting a company."
      >
        <TextArea
          value={stations.join('\n')}
          onChange={(e) => setStations(e.target.value)}
          rows={6}
          placeholder={'Sydney HQ\nMelbourne West\nBrisbane North'}
        />
      </Field>
    </div>
  );
}

function QuestionCategoriesPanel() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['question-categories'],
    queryFn: () =>
      adminApi.get<{ categories: QuestionCategory[] }>('/api/question-categories'),
  });

  const [name, setName] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [error, setError] = useState<unknown>(null);

  const categories = data?.categories ?? [];

  const createMutation = useMutation({
    mutationFn: (input: { name: string }) =>
      adminApi.post('/api/question-categories', input),
    onSuccess: () => {
      setName('');
      void qc.invalidateQueries({ queryKey: ['question-categories'] });
    },
    onError: (err) => setError(err),
  });

  const updateMutation = useMutation({
    mutationFn: (input: { id: string; name: string }) =>
      adminApi.patch(`/api/question-categories/${input.id}`, { name: input.name }),
    onSuccess: () => {
      setEditingId(null);
      setEditingName('');
      void qc.invalidateQueries({ queryKey: ['question-categories'] });
    },
    onError: (err) => setError(err),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminApi.delete(`/api/question-categories/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['question-categories'] }),
    onError: (err) => setError(err),
  });

  const reorderMutation = useMutation({
    mutationFn: (ids: string[]) =>
      adminApi.post('/api/question-categories/reorder', { ids }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['question-categories'] }),
    onError: (err) => setError(err),
  });

  function move(id: string, dir: -1 | 1) {
    const ids = categories.map((c) => c.id);
    const idx = ids.indexOf(id);
    const next = idx + dir;
    if (idx < 0 || next < 0 || next >= ids.length) return;
    [ids[idx], ids[next]] = [ids[next]!, ids[idx]!];
    reorderMutation.mutate(ids);
  }

  return (
    <div className="space-y-3">
      <div>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
          Question categories
        </h2>
        <p className="mt-1 text-xs text-slate-500">
          Categories group questions in the builder and questionnaire. Parent/child links may
          cross categories — pick any question as a parent when setting up conditional logic.
        </p>
      </div>

      <form
        className="flex items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          const trimmed = name.trim();
          if (!trimmed) return;
          createMutation.mutate({ name: trimmed });
        }}
      >
        <div className="flex-1">
          <Field label="Add category">
            <TextInput
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Vehicle safety"
            />
          </Field>
        </div>
        <Btn type="submit" loading={createMutation.isPending} disabled={!name.trim()}>
          Add
        </Btn>
      </form>

      {isLoading ? (
        <CenteredSpinner />
      ) : categories.length === 0 ? (
        <EmptyState
          title="No categories yet"
          description="Add a category above to start organising questions."
        />
      ) : (
        <ul className="divide-y divide-slate-200 rounded-lg border border-slate-200">
          {categories.map((cat, i) => (
            <li key={cat.id} className="flex items-center gap-3 px-3 py-2">
              <div className="flex flex-col">
                <button
                  onClick={() => move(cat.id, -1)}
                  disabled={i === 0}
                  className="px-1 text-slate-400 hover:text-slate-700 disabled:opacity-30"
                  aria-label="Move up"
                >
                  ▲
                </button>
                <button
                  onClick={() => move(cat.id, 1)}
                  disabled={i === categories.length - 1}
                  className="px-1 text-slate-400 hover:text-slate-700 disabled:opacity-30"
                  aria-label="Move down"
                >
                  ▼
                </button>
              </div>
              <div className="flex-1">
                {editingId === cat.id ? (
                  <TextInput
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && editingName.trim()) {
                        updateMutation.mutate({ id: cat.id, name: editingName.trim() });
                      } else if (e.key === 'Escape') {
                        setEditingId(null);
                      }
                    }}
                    autoFocus
                  />
                ) : (
                  <span className="text-sm text-slate-900">{cat.name}</span>
                )}
              </div>
              {editingId === cat.id ? (
                <>
                  <Btn
                    size="sm"
                    onClick={() =>
                      updateMutation.mutate({ id: cat.id, name: editingName.trim() })
                    }
                    loading={updateMutation.isPending}
                    disabled={!editingName.trim()}
                  >
                    Save
                  </Btn>
                  <Btn size="sm" variant="ghost" onClick={() => setEditingId(null)}>
                    Cancel
                  </Btn>
                </>
              ) : (
                <>
                  <Btn
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      setEditingId(cat.id);
                      setEditingName(cat.name);
                    }}
                  >
                    Rename
                  </Btn>
                  <Btn
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      if (confirm(`Delete category "${cat.name}"?`)) deleteMutation.mutate(cat.id);
                    }}
                  >
                    Delete
                  </Btn>
                </>
              )}
            </li>
          ))}
        </ul>
      )}

      <ErrorMessage error={error} />
    </div>
  );
}

function ColourField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string | undefined;
  onChange: (v: string) => void;
}) {
  return (
    <Field label={label}>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={value && HEX_RE.test(value) ? value : '#000000'}
          onChange={(e) => onChange(e.target.value)}
          className="h-9 w-12 rounded border border-slate-200"
        />
        <TextInput value={value ?? ''} onChange={(e) => onChange(e.target.value)} />
      </div>
    </Field>
  );
}
